import { useEffect, useMemo, useState, useRef } from "react";
import { api, TYPE_LABELS, money } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Calculator, Save, RefreshCw, Receipt, TrendingUp } from "lucide-react";
import { Input } from "@/components/ui/input";

const pct = (n) => `${Number(n || 0).toFixed(1)}%`;
const marginTone = (m) => (m >= 40 ? "text-emerald-600" : m >= 20 ? "text-amber-600" : "text-rose-600");
const band = (v, rng) => (!rng || !(v > 0) ? null : v < rng[0] ? "below" : v > rng[1] ? "above" : "within");
const BAND_STYLE = {
  below: { cls: "bg-amber-50 text-amber-700", label: "Below" },
  within: { cls: "bg-emerald-50 text-emerald-700", label: "In range" },
  above: { cls: "bg-blue-50 text-blue-700", label: "Above" },
};

// Direct-to-consumer profit on one unit: retail minus payment processing (% + fixed) and Printify cost.
function retailEconomics(retail, cost, procPct, procFixed) {
  const processing = retail > 0 ? retail * (procPct / 100) + procFixed : 0;
  const profit = retail - processing - cost;
  const margin = retail > 0 ? (profit / retail) * 100 : 0;
  return { profit, margin };
}

// Margin if you KEEP today's price and just absorb shipping — i.e. offer free shipping with no price change.
function freeShipMargin(retail, cost, ship, procPct, procFixed) {
  if (!(retail > 0)) return 0;
  const processing = retail * (procPct / 100) + procFixed;
  return ((retail - processing - cost - ship) / retail) * 100;
}

export default function RetailMargins() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [procPct, setProcPct] = useState(2.9);
  const [procFixed, setProcFixed] = useState(0.3);
  const [target, setTarget] = useState(40);
  const [includeShip, setIncludeShip] = useState(false);
  const [buffer, setBuffer] = useState(5);
  const [typeFilter, setTypeFilter] = useState("");
  const [onlyBelow, setOnlyBelow] = useState(false);
  const [applying, setApplying] = useState(false);
  const [rebasePreview, setRebasePreview] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [showSnapshots, setShowSnapshots] = useState(false);
  const pollRef = useRef(null);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const pollJob = (jobId, doneMsg) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/jobs/${jobId}`);
        if (["done", "error"].includes(j.status)) {
          clearInterval(pollRef.current);
          setApplying(false);
          if (j.status === "done") toast.success(`${doneMsg} — ${j.result?.updated || 0} product(s)`);
          else toast.error("Job hit an error — check logs");
          load();
          loadSnapshots();
        }
      } catch { clearInterval(pollRef.current); setApplying(false); }
    }, 2500);
  };

  const loadSnapshots = async () => {
    try {
      const { data } = await api.get("/wholesale/price-snapshots");
      setSnapshots(data.snapshots || []);
    } catch { setSnapshots([]); }
  };
  useEffect(() => { loadSnapshots(); }, []);

  const openRebaseline = async () => {
    try {
      const { data } = await api.get(`/pricing/rebaseline-preview?target_margin=${Number(target)}&include_shipping=${includeShip}&buffer_pct=${Number(buffer)}`);
      if (!data.count) { toast.success("Every product is already at your commercial baseline — nothing to change."); return; }
      setRebasePreview(data);
    } catch { toast.error("Could not build rebaseline preview"); }
  };

  const runRebaseline = async () => {
    const items = (rebasePreview?.items || []).map((i) => ({ id: i.id, wholesale_price: i.new_ws, retail_price: i.new_retail }));
    if (!items.length) return;
    setApplying(true);
    setRebasePreview(null);
    try {
      const { data } = await api.post("/wholesale/apply-pricing", { items });
      toast.info(`Rebaselining ${data.count} product(s) & syncing to Shopify…`);
      pollJob(data.job_id, "Retail rebaselined");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rebaseline");
      setApplying(false);
    }
  };

  const revertSnapshot = async (snap) => {
    if (!window.confirm(`Revert ${snap.count} product(s) back to their prices from "${snap.label}" (${new Date(snap.created_at).toLocaleString()})? This restores the previous retail + wholesale and re-syncs to Shopify.`)) return;
    setApplying(true);
    setShowSnapshots(false);
    try {
      const { data } = await api.post(`/wholesale/revert/${snap.id}`);
      toast.info(`Reverting ${data.count} product(s)…`);
      pollJob(data.job_id, "Reverted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revert");
      setApplying(false);
    }
  };

  const load = async (refresh = false) => {
    try {
      const { data } = await api.get(`/retail/margins${refresh ? "?refresh=true" : ""}`);
      setData(data);
      const d = data.defaults || {};
      setProcPct(d.processing_pct ?? 2.9);
      setProcFixed(d.processing_fixed ?? 0.3);
      setTarget(d.target_margin ?? 40);
      setIncludeShip(!!d.include_shipping);
      if (d.buffer_pct != null) setBuffer(d.buffer_pct);
      if (data.econ_error) toast.warning(data.econ_error);
    } catch (e) {
      toast.error("Could not load retail margins");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []);

  const refresh = () => { setRefreshing(true); load(true); };

  const saveDefaults = async () => {
    setSaving(true);
    try {
      await api.post("/retail/settings", {
        processing_pct: Number(procPct), processing_fixed: Number(procFixed), target_margin: Number(target),
        include_shipping: includeShip, buffer_pct: Number(buffer),
      });
      toast.success("Saved as defaults");
      load();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const pp = Number(procPct), pf = Number(procFixed), t = Number(target);
    let list = data.rows.map((r) => {
      const { profit, margin } = retailEconomics(r.retail, r.unit_cost, pp, pf);
      const bm = data.benchmarks?.[r.product_type];
      const fsMargin = freeShipMargin(r.retail, r.unit_cost, Number(r.ship_first || 0), pp, pf);
      return {
        ...r,
        unitProfit: profit,
        unitMargin: margin,
        freeShipMargin: fsMargin,
        freeShipOk: fsMargin >= t - 0.05,
        belowTarget: margin < t - 0.05,
        bmRetail: bm?.retail,
        retailBand: band(r.retail, bm?.retail),
      };
    });
    if (typeFilter) list = list.filter((r) => r.product_type === typeFilter);
    if (onlyBelow) list = list.filter((r) => r.belowTarget);
    return list;
  }, [data, procPct, procFixed, target, typeFilter, onlyBelow]);

  const totals = useMemo(() => {
    if (!rows.length) return null;
    const avgMargin = rows.reduce((s, r) => s + r.unitMargin, 0) / rows.length;
    const avgProfit = rows.reduce((s, r) => s + r.unitProfit, 0) / rows.length;
    const below = rows.filter((r) => r.belowTarget).length;
    const negative = rows.filter((r) => r.unitProfit <= 0).length;
    return { avgMargin, avgProfit, below, negative, count: rows.length };
  }, [rows]);

  const types = data ? [...new Set(data.rows.map((r) => r.product_type))] : [];

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="max-w-[1500px] mx-auto" data-testid="retail-margins-page">
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-blue-600">Honorifica.com · Direct-to-consumer</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Retail Margins</h1>
          <p className="text-sm text-zinc-500 mt-1">Real Printify cost vs. your consumer retail price, after payment processing only (no Faire commission) — profit &amp; margin per unit sold on your store.</p>
        </div>
        <button onClick={refresh} disabled={refreshing} data-testid="retail-refresh-costs-btn" className="h-9 px-3 rounded-md border border-zinc-300 hover:bg-zinc-100 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh live costs
        </button>
      </div>

      {/* Assumptions bar */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-4" data-testid="retail-assumptions-bar">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><Calculator className="w-4 h-4 text-blue-600" /> Assumptions</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Payment processing %", val: procPct, set: setProcPct, tid: "retail-input-processing", step: "0.1" },
            { label: "Processing fixed fee ($/order)", val: procFixed, set: setProcFixed, tid: "retail-input-fixed", step: "0.01" },
            { label: "Target retail margin %", val: target, set: setTarget, tid: "retail-input-target", step: "0.1" },
            { label: "Returns/ads buffer %", val: buffer, set: setBuffer, tid: "retail-input-buffer", step: "0.1" },
          ].map((f) => (
            <label key={f.tid} className="block">
              <span className="text-[11px] text-zinc-500">{f.label}</span>
              <Input type="number" step={f.step} value={f.val} onChange={(e) => f.set(e.target.value)} data-testid={f.tid} className="mt-1 h-9" />
            </label>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <button onClick={() => setIncludeShip((v) => !v)} data-testid="retail-freeship-toggle"
            className={`h-9 px-3 rounded-md text-xs font-semibold border transition-colors flex items-center gap-1.5 ${includeShip ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-zinc-300 text-zinc-600 hover:bg-zinc-100"}`}>
            <TrendingUp className="w-3.5 h-3.5" /> Free-shipping pricing: {includeShip ? "ON" : "OFF"}
          </button>
          <span className="text-[11px] text-zinc-500 max-w-xl">
            {includeShip
              ? "Rebaseline now bakes live per-type domestic shipping + buffer into the price so you can advertise FREE shipping. The “Free-ship now” column shows which products already clear your target at today’s price — those need no price change."
              : "OFF: prices assume the customer pays shipping at checkout (current behavior). Turn on to price for free domestic shipping."}
          </span>
          <button onClick={saveDefaults} disabled={saving} data-testid="retail-save-defaults-btn" className="ml-auto h-9 px-4 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
        <p className="text-[11px] text-zinc-400 mt-3 border-t border-zinc-100 pt-3">Direct-to-consumer sales on Honorifica.com only pay payment processing (e.g. Shopify Payments ~2.9% + $0.30) — no Faire commission. This view never changes prices; it's read-only insight into your store margins.</p>
      </div>

      {/* Totals */}
      {totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="retail-margin-totals">
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <div className="text-[11px] text-zinc-500">Avg. unit margin</div>
            <div className={`text-2xl font-bold mt-1 ${marginTone(totals.avgMargin)}`}>{pct(totals.avgMargin)}</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <div className="text-[11px] text-zinc-500">Avg. profit / unit</div>
            <div className={`text-2xl font-bold mt-1 ${totals.avgProfit < 0 ? "text-rose-600" : "text-emerald-600"}`}>{money(totals.avgProfit)}</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <div className="text-[11px] text-zinc-500">SKUs below {Number(target)}% target</div>
            <div className={`text-2xl font-bold mt-1 ${totals.below ? "text-amber-600" : "text-emerald-600"}`}>{totals.below}</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <div className="text-[11px] text-zinc-500">SKUs at a loss</div>
            <div className={`text-2xl font-bold mt-1 ${totals.negative ? "text-rose-600" : "text-emerald-600"}`}>{totals.negative}</div>
          </div>
        </div>
      )}

      {/* Type filter */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="retail-type-filter" className="bg-white border border-zinc-200 rounded-md px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All product types</option>
          {types.map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
        </select>
        <button onClick={() => setOnlyBelow((v) => !v)} data-testid="retail-toggle-below-target" className={`h-8 px-3 rounded-md text-xs font-medium border transition-colors ${onlyBelow ? "bg-amber-600 border-amber-600 text-white" : "bg-white border-zinc-300 text-zinc-600 hover:bg-zinc-100"}`}>
          {onlyBelow ? "Showing below-target only" : "Show below-target only"}
        </button>
        <span className="text-xs text-zinc-400">{rows.length} items</span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={openRebaseline} disabled={applying} data-testid="rebaseline-btn"
            className="h-8 px-3 rounded-md text-xs font-semibold bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-1.5 disabled:opacity-50 transition-colors"
            title={`Reset every product's retail to a commercial baseline (market midpoint or ${Number(target)}% DTC margin, whichever is higher). WS follows at 50%.`}>
            {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
            Rebaseline all retail (@{Number(target)}% floor)
          </button>
          <div className="relative">
            <button onClick={() => { setShowSnapshots((v) => !v); loadSnapshots(); }} data-testid="retail-undo-menu-btn"
              className="h-8 px-3 rounded-md text-xs font-medium border border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-100 transition-colors">
              Undo recent change{snapshots.length ? ` (${snapshots.length})` : ""}
            </button>
            {showSnapshots && (
              <div className="absolute right-0 mt-1 w-80 max-h-80 overflow-y-auto bg-white border border-zinc-200 rounded-lg shadow-lg z-20 p-2" data-testid="retail-snapshots-menu">
                {snapshots.length === 0 ? (
                  <div className="text-xs text-zinc-400 px-2 py-3 text-center">No recent price changes to undo.</div>
                ) : snapshots.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-zinc-50 rounded">
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-zinc-700 truncate">{s.label}</div>
                      <div className="text-[10px] text-zinc-400">{s.count} items · {new Date(s.created_at).toLocaleString()}</div>
                    </div>
                    <button onClick={() => revertSnapshot(s)} disabled={applying} data-testid={`retail-revert-${s.id}`}
                      className="h-6 px-2 rounded text-[11px] font-medium bg-zinc-900 hover:bg-zinc-800 text-white disabled:opacity-50 shrink-0">Revert</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {rebasePreview && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="rebaseline-modal" onClick={() => setRebasePreview(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[80vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-zinc-100">
              <h3 className="text-sm font-bold text-zinc-800">Rebaseline retail to commercial prices</h3>
              <p className="text-xs text-zinc-500 mt-0.5">{rebasePreview.count} product(s) will change. Retail = market midpoint or the price for a {rebasePreview.target_margin}% DTC margin (whichever is higher), charm-priced. Wholesale follows at 50% (never below cost). This changes consumer + wholesale prices and re-syncs to Shopify. You can undo it afterward.</p>
              {rebasePreview.include_shipping && (
                <p className="text-xs text-sky-700 mt-1.5 bg-sky-50 border border-sky-200 rounded px-2 py-1.5" data-testid="rebaseline-freeship-note">
                  <b>Free-shipping pricing ON</b> — live per-type domestic shipping{rebasePreview.buffer_pct ? ` + ${rebasePreview.buffer_pct}% returns/ads buffer` : ""} is baked into each price, so you can turn on free shipping. The margin shown is your <b>true</b> margin after absorbing shipping. Prices only rise where today’s price can’t already cover it.
                </p>
              )}
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              <table className="w-full text-xs">
                <thead className="text-zinc-400 sticky top-0 bg-white">
                  <tr><th className="text-left px-2 py-1.5">Product</th><th className="text-right px-2 py-1.5">Retail</th><th className="text-right px-2 py-1.5">WS</th><th className="text-right px-2 py-1.5">DTC margin</th></tr>
                </thead>
                <tbody>
                  {rebasePreview.items.map((i) => (
                    <tr key={i.id} className="border-t border-zinc-50" data-testid={`rebaseline-item-${i.id}`}>
                      <td className="px-2 py-1.5"><div className="font-medium text-zinc-700 truncate max-w-[280px]">{i.title}</div><div className="text-[10px] text-zinc-400">{TYPE_LABELS[i.product_type] || i.product_type}</div></td>
                      <td className="text-right px-2 py-1.5 tabular-nums"><span className="text-zinc-400 line-through">{money(i.current_retail)}</span> <span className="font-semibold text-emerald-700">{money(i.new_retail)}</span></td>
                      <td className="text-right px-2 py-1.5 tabular-nums"><span className="text-zinc-400 line-through">{money(i.current_ws)}</span> <span className="font-semibold text-emerald-700">{money(i.new_ws)}</span></td>
                      <td className={`text-right px-2 py-1.5 tabular-nums font-semibold ${i.dtc_margin >= 45 ? "text-emerald-600" : "text-amber-600"}`}>{i.dtc_margin}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-zinc-100 flex items-center justify-end gap-2">
              <button onClick={() => setRebasePreview(null)} data-testid="rebaseline-cancel-btn" className="h-9 px-4 rounded-md border border-zinc-300 text-sm text-zinc-600 hover:bg-zinc-50">Cancel</button>
              <button onClick={runRebaseline} data-testid="rebaseline-apply-btn" className="h-9 px-4 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold">Apply to {rebasePreview.count} & sync</button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto" data-testid="retail-margins-table">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left font-medium px-4 py-3">Product</th>
              <th className="text-right font-medium px-3 py-3">Retail</th>
              <th className="text-right font-medium px-3 py-3">Unit cost</th>
              <th className="text-right font-medium px-3 py-3">Processing</th>
              <th className="text-right font-medium px-3 py-3">Unit profit</th>
              <th className="text-right font-medium px-3 py-3">Margin</th>
              {includeShip && <th className="text-right font-medium px-3 py-3">Free-ship now<br/><span className="normal-case text-[10px] text-zinc-400">margin if absorbed</span></th>}
              <th className="text-center font-medium px-3 py-3">Market<br/><span className="normal-case text-[10px] text-zinc-400">Amazon retail</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((r) => {
              const processing = r.retail > 0 ? r.retail * (Number(procPct) / 100) + Number(procFixed) : 0;
              return (
                <tr key={r.id} data-testid={`retail-row-${r.id}`} className={`hover:bg-zinc-50/70 ${r.belowTarget ? (r.unitProfit <= 0 ? "bg-rose-50/50" : "bg-amber-50/50") : ""}`}>
                  <td className="px-4 py-3 max-w-[380px]">
                    <div className="font-medium truncate">{r.title}</div>
                    <div className="text-[11px] text-zinc-400 flex items-center gap-1">
                      {TYPE_LABELS[r.product_type] || r.product_type}
                      {r.cost_is_estimate && <span className="text-amber-600" title="Using estimated cost (no live Printify listing for this type)">· est.</span>}
                    </div>
                  </td>
                  <td className="text-right px-3 py-3 tabular-nums">{money(r.retail)}</td>
                  <td className="text-right px-3 py-3 tabular-nums text-zinc-500">{money(r.unit_cost)}</td>
                  <td className="text-right px-3 py-3 tabular-nums text-zinc-500">{money(processing)}</td>
                  <td className={`text-right px-3 py-3 tabular-nums font-semibold ${r.unitProfit <= 0 ? "text-rose-600" : "text-zinc-800"}`}>{money(r.unitProfit)}</td>
                  <td className={`text-right px-3 py-3 tabular-nums font-semibold ${marginTone(r.unitMargin)}`}>{pct(r.unitMargin)}</td>
                  {includeShip && (
                    <td className="text-right px-3 py-3 tabular-nums" data-testid={`retail-freeship-${r.id}`} title={`If you keep ${money(r.retail)} and absorb ${money(r.ship_first || 0)} shipping`}>
                      <span className={`font-semibold ${marginTone(r.freeShipMargin)}`}>{pct(r.freeShipMargin)}</span>
                      {r.freeShipOk
                        ? <div className="text-[10px] text-emerald-600">free ship OK now</div>
                        : <div className="text-[10px] text-amber-600">rebaseline to cover</div>}
                    </td>
                  )}
                  <td className="text-center px-3 py-3" data-testid={`retail-market-${r.id}`}>
                    {r.retailBand ? (
                      <span className={`inline-flex items-center rounded-full text-[10px] font-medium px-2 py-0.5 ${BAND_STYLE[r.retailBand].cls}`} title={r.bmRetail ? `Retail market ${money(r.bmRetail[0])}–${money(r.bmRetail[1])} (Amazon)` : ""}>
                        {BAND_STYLE[r.retailBand].label}
                      </span>
                    ) : <span className="text-[10px] text-zinc-300">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="py-16 text-center text-sm text-zinc-400 flex flex-col items-center gap-2"><Receipt className="w-8 h-8 text-zinc-300" /> No products to analyze yet.</div>
        )}
      </div>
      <p className="text-[11px] text-zinc-400 mt-3">Margin = (retail − payment processing − Printify product cost{includeShip ? " − absorbed shipping − buffer" : ""}) ÷ retail. {includeShip ? "Free-shipping pricing is ON: the “Free-ship now” column shows your margin if you absorb shipping at today’s price — green means you can offer free shipping with no price change." : "Shipping is assumed passed to the customer at checkout."} Costs are pulled live from your Printify listings.</p>
    </div>
  );
}
